import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, RefreshCcw, Printer, ChevronDown, ChevronUp } from "lucide-react";

const BASE_URL = "http://nissi-india.com";

const months = [
  { text: "Select", value: "Select" },
  { text: "January", value: "1" },
  { text: "February", value: "2" },
  { text: "March", value: "3" },
  { text: "April", value: "4" },
  { text: "May", value: "5" },
  { text: "June", value: "6" },
  { text: "July", value: "7" },
  { text: "August", value: "8" },
  { text: "September", value: "9" },
  { text: "October", value: "10" },
  { text: "November", value: "11" },
  { text: "December", value: "12" },
];

const toNum = (v) => {
  const n = Number((v ?? "").toString().replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const SalaryAbstractReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const screenBadge = useMemo(() => "INFRA SCREEN : C-39", []);

  const finYearId = localStorage.getItem("finYearId") || "";
  const loginBranchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  // meta
  const [branches, setBranches] = useState([]);
  const [years, setYears] = useState([]);

  // filters
  const [branchId, setBranchId] = useState("Select");
  const [monthValue, setMonthValue] = useState("Select");
  const [year, setYear] = useState("Select");

  const [isPF, setIsPF] = useState(""); // yes/no/Honorarium
  const [payMode, setPayMode] = useState(""); // Bank/Cash

  // list
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    grossPay: 0,
    pf: 0,
    advance: 0,
    loan: 0,
    esi: 0,
    tds: 0,
    netpay: 0,
  });
  const [loading, setLoading] = useState(false);

  // ui
  const [showFilters, setShowFilters] = useState(true);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const monthName = useMemo(() => {
    const m = months.find((x) => x.value === String(monthValue));
    return m?.text || "Select";
  }, [monthValue]);

  const fetchMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(loginBranchId).trim() || !String(branchType).trim()) {
      toast.warn("Missing branchid/branchtype in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-abstract-report/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(loginBranchId),
          branchType: String(branchType),
        },
      });

      if (res?.data?.success) {
        setBranches(res.data.branches || []);
        setYears(res.data.years || []);

        setBranchId(String(res.data.defaultBranchId || "Select"));
        setYear(String(res.data.defaultYear || "Select"));
        setMonthValue(String(res.data.defaultMonthValue || "Select"));

        // default in ASP: PF and Paymode not pre-checked => pass empty strings
        setIsPF("");
        setPayMode("");

        // initial grid load
        await fetchData({
          branchId: String(res.data.defaultBranchId || "Select"),
          year: String(res.data.defaultYear || "Select"),
          monthName: months.find((x) => x.value === String(res.data.defaultMonthValue))?.text || "Select",
          isPF: "",
          payMode: "",
        });
      } else {
        toast.error(res?.data?.message || "Failed to load meta.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (override = null) => {
    if (!String(finYearId).trim()) return;

    const payload = override || {
      branchId,
      year,
      monthName,
      isPF,
      payMode,
    };

    if (payload.monthName === "Select" || payload.year === "Select") {
      // ASP loads grid even without validation sometimes, but better UX to keep strict
      setRows([]);
      setTotals({ grossPay: 0, pf: 0, advance: 0, loan: 0, esi: 0, tds: 0, netpay: 0 });
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/salary-abstract-report/data`, {
        params: {
          finYearId: Number(finYearId),
          branchType: String(branchType),
          loginBranchId: Number(loginBranchId),
          branchId: payload.branchId,
          monthName: payload.monthName,
          year: payload.year,
          isPF: payload.isPF,
          payMode: payload.payMode,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotals(res.data.totals || { grossPay: 0, pf: 0, advance: 0, loan: 0, esi: 0, tds: 0, netpay: 0 });
      } else {
        toast.error(res?.data?.message || "Failed to load report.");
        setRows([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "API error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    if (monthName === "Select" || year === "Select") {
      toast.warn("Select Month and Year");
      return;
    }
    await fetchData();
  };

  const handlePrint = () => {
    if (monthName === "Select" || year === "Select") {
      toast.warn("Select Month and Year");
      return;
    }
    const qs =
      `finYearId=${encodeURIComponent(Number(finYearId))}` +
      `&branchType=${encodeURIComponent(String(branchType))}` +
      `&loginBranchId=${encodeURIComponent(Number(loginBranchId))}` +
      `&branchId=${encodeURIComponent(branchId)}` +
      `&monthName=${encodeURIComponent(monthName)}` +
      `&year=${encodeURIComponent(year)}` +
      `&isPF=${encodeURIComponent(isPF)}` +
      `&payMode=${encodeURIComponent(payMode)}`;

    window.open(`/PayRoll/SalaryAbstractReport_Print?${qs}`, "_blank");
  };

  return (
    <PayRollLayoutWrapper>
      <ToastContainer position="top-right" autoClose={2800} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">SALARY ABSTRACT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Salary Abstract (Department-wise)
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Branch • PF • Month • Year • Pay Mode • Totals Footer
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {rows.length} Rows
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchData()} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handlePrint} className={ghostBtn} disabled={loading}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                <div>
                  <label className={labelBase}>Branch</label>
                  <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>PF</label>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="ispf" checked={isPF === "yes"} onChange={() => setIsPF("yes")} />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="ispf" checked={isPF === "no"} onChange={() => setIsPF("no")} />
                      <span>No</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="ispf" checked={isPF === "Honorarium"} onChange={() => setIsPF("Honorarium")} />
                      <span>Honorarium</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelBase}>Month</label>
                  <select value={monthValue} onChange={(e) => setMonthValue(e.target.value)} className={inputBase}>
                    {months.map((m) => (
                      <option key={m.value} value={m.value}>{m.text}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Year</label>
                  <select value={year} onChange={(e) => setYear(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {years.map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Payment Mode</label>
                  <div className="mt-2 flex flex-wrap gap-4">
                    <label className="flex items-center gap-2">
                      <input type="radio" name="paymode" checked={payMode === "Cash"} onChange={() => setPayMode("Cash")} />
                      <span>Cash</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input type="radio" name="paymode" checked={payMode === "Bank"} onChange={() => setPayMode("Bank")} />
                      <span>Bank</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex justify-center mt-6">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">Gross Salary</th>
                    <th className="px-4 py-4 text-center font-bold">PF</th>
                    <th className="px-4 py-4 text-center font-bold">Others</th>
                    <th className="px-4 py-4 text-center font-bold">Advance</th>
                    <th className="px-4 py-4 text-center font-bold">ESI</th>
                    <th className="px-4 py-4 text-center font-bold">TDS</th>
                    <th className="px-4 py-4 text-center font-bold">Net Salary</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 9 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={9} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr key={idx} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{idx + 1}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_departmentname}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.grossPay}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.pf}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.advance}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.loan}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.esi}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.tds}</td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.netpay}</td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* footer totals (ASP footer templates) */}
                {!loading && rows.length > 0 && (
                  <tfoot>
                    <tr className={`${isDark ? "bg-gray-950" : "bg-gray-50"} font-extrabold`}>
                      <td className="px-4 py-4 text-center" />
                      <td className="px-4 py-4 text-center">Total</td>
                      <td className="px-4 py-4 text-center">{totals.grossPay}</td>
                      <td className="px-4 py-4 text-center">{totals.pf}</td>
                      <td className="px-4 py-4 text-center">{totals.advance}</td>
                      <td className="px-4 py-4 text-center">{totals.loan}</td>
                      <td className="px-4 py-4 text-center">{totals.esi}</td>
                      <td className="px-4 py-4 text-center">{totals.tds}</td>
                      <td className="px-4 py-4 text-center">{totals.netpay}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default SalaryAbstractReport;
